import { lazy, Suspense, useMemo } from "react";
import BabybjornUspsModule from "../../src/modules/babybjorn-usps-module";
import CategoryUspModule from "../../src/modules/category-usps-module";
import ComparisonTableModule from "../../src/modules/comparison-table-module";
import ContentSlideModule from "../../src/modules/content-slide-module";
import FilterModule from "../../src/modules/filter-module";
import GalleryModule from "../../src/modules/gallery-module";
import HeadlineModule from "../../src/modules/headline-module";
import ImageAndTextModule from "../../src/modules/image-and-text-module";
import NavListModule from "../../src/modules/product-tab/nav-list";
import UspListModule from "../../src/modules/product-tab/usp-list";
import ProductsSlideModule from "../../src/modules/products-slide-module";
import QuickLinksModule from "../../src/modules/quick-links-module";
import RichtextContainerModule from "../../src/modules/richtext-container-module";
import TextModule from "../../src/modules/text-module";
import TextOnContentModule from "../../src/modules/text-on-content-module";
import ThreeUpInspirationModule from "../../src/modules/three-up-inspiration-module";
import ThreeUpModule from "../../src/modules/three-up-module";
import TwoUpModule from "../../src/modules/two-up-module";
import DoubleTextOnContentModule from "../modules/double-text-on-content-module";
import EmbedSocialModule from "../modules/embed-social-module";
import ReviewsModule from "../modules/reviews-module";
import TabsModule from "../modules/tabs-module";
import LinkListsModule from "../modules/link-lists-module";
import CarryPositionsModule from "../modules/product-tab/carry-positions-module";
import {
	filterByStore,
	type StoreItem,
} from "../utils/storyblok/store-filters";
import { useAppState } from "../context/app/context";

const LottiePlayerModule = lazy(
	() => import("../modules/lottie-player-module"),
);

export type ModuleRendererProps = {
	modules: (
		| undefined
		| null
		| Queries.TextOnContentModuleFragment
		| Queries.ProductsSlideModuleFragment
		| Queries.DoubleTextOnContentModuleFragment
		| Queries.QuickLinksModuleFragment
		| Queries.TabsModuleFragment
		| Queries.EmbedSocialModuleFragment
		| Queries.CategoryUspsModuleFragment
		| Queries.ThreeUpModuleFragment
		| Queries.ReviewsModuleFragment
		| Queries.ImageAndTextModuleFragment
		| Queries.BabyBjornUspsModuleFragment
		| Queries.TextModuleFragment
		| Queries.ContentSlideModuleFragment
		| Queries.GalleryModuleFragment
		| Queries.ComparisonTableModuleFragment
		| Queries.ThreeUpInspirationModuleFragment
		| Queries.RichtextContainerModuleFragment
		| Queries.HeadlineModuleFragment
		| Queries.NavListFragment
		| Queries.FilterModuleFragment
		| Queries.TwoUpModuleFragment
		| Queries.LottiePlayerModuleFragment
		| Queries.LinkListsModuleFragment
		| Queries.UspListFragment
		| Queries.CarryPositionsFragment
	)[];
};

const ModuleRenderer: React.FC<ModuleRendererProps> = ({ modules }) => {
	const { siteConfig } = useAppState();
	const moduleComponents = useMemo(() => {
		let linkIdIndex = -1;
		return modules
			.filter((m) => filterByStore(m as StoreItem, siteConfig.store))
			.map((module) => {
				if (!module?.component) return null;
				switch (module.component) {
					case "text_on_content_module": {
						linkIdIndex++;
						return (
							<TextOnContentModule
								key={module._uid}
								{...(module as Queries.TextOnContentModuleFragment)}
								linkIdIndex={linkIdIndex}
							/>
						);
					}
					case "quick_links_module": {
						return (
							<QuickLinksModule
								key={module._uid}
								{...(module as Queries.QuickLinksModuleFragment)}
							/>
						);
					}
					case "products_slide_module": {
						return (
							<ProductsSlideModule
								key={module._uid}
								{...(module as Queries.ProductsSlideModuleFragment)}
							/>
						);
					}
					case "double_text_on_content_module": {
						linkIdIndex += 2;
						return (
							<DoubleTextOnContentModule
								key={module._uid}
								{...(module as Queries.DoubleTextOnContentModuleFragment)}
								linkIdIndex={linkIdIndex}
							/>
						);
					}
					case "tabs_module": {
						return (
							<TabsModule
								key={module._uid}
								{...(module as Queries.TabsModuleFragment)}
							/>
						);
					}
					case "embed_social_module": {
						return (
							<EmbedSocialModule
								key={module._uid}
								{...(module as Queries.EmbedSocialModuleFragment)}
							/>
						);
					}
					case "category_usps_module": {
						return (
							<CategoryUspModule
								key={module._uid}
								{...(module as Queries.CategoryUspsModuleFragment)}
							/>
						);
					}
					case "three_up_module": {
						return (
							<ThreeUpModule
								key={module._uid}
								{...(module as Queries.ThreeUpModuleFragment)}
							/>
						);
					}
					case "reviews_module": {
						return (
							<ReviewsModule
								key={module._uid}
								{...(module as Queries.ReviewsModuleFragment)}
							/>
						);
					}
					case "babybjorn_usps_module": {
						return (
							<BabybjornUspsModule
								key={module._uid}
								{...(module as Queries.BabyBjornUspsModuleFragment)}
							/>
						);
					}
					case "text_module": {
						return (
							<TextModule
								key={module._uid}
								{...(module as Queries.TextModuleFragment)}
							/>
						);
					}
					case "content_slide_module": {
						return (
							<ContentSlideModule
								key={module._uid}
								{...(module as Queries.ContentSlideModuleFragment)}
							/>
						);
					}
					case "gallery_module": {
						return (
							<GalleryModule
								key={module._uid}
								{...(module as Queries.GalleryModuleFragment)}
							/>
						);
					}
					case "comparison_table_module": {
						return (
							<ComparisonTableModule
								key={module._uid}
								{...(module as Queries.ComparisonTableModuleFragment)}
							/>
						);
					}
					case "image_and_text_module": {
						return (
							<ImageAndTextModule
								key={module._uid}
								{...(module as Queries.ImageAndTextModuleFragment)}
							/>
						);
					}
					case "three_up_inspiration_module": {
						return (
							<ThreeUpInspirationModule
								key={module._uid}
								{...(module as Queries.ThreeUpInspirationModuleFragment)}
							/>
						);
					}
					case "richtext_container_module": {
						return (
							<RichtextContainerModule
								key={module._uid}
								{...(module as Queries.RichtextContainerModuleFragment)}
							/>
						);
					}
					case "headline_module": {
						return (
							<HeadlineModule
								key={module._uid}
								{...(module as Queries.HeadlineModuleFragment)}
							/>
						);
					}
					case "nav_list": {
						return (
							<NavListModule
								key={module._uid}
								{...(module as Queries.NavListFragment)}
							/>
						);
					}
					case "usp_list": {
						return (
							<UspListModule
								key={module._uid}
								{...(module as Queries.UspListFragment)}
							/>
						);
					}
					case "filter_module": {
						return (
							<FilterModule
								key={module._uid}
								{...(module as Queries.FilterModuleFragment)}
							/>
						);
					}
					case "two_up_module": {
						return (
							<TwoUpModule
								key={module._uid}
								{...(module as Queries.TwoUpModuleFragment)}
							/>
						);
					}
					case "link_lists_module": {
						return (
							<LinkListsModule
								key={module._uid}
								{...(module as Queries.LinkListsModuleFragment)}
							/>
						);
					}
					case "lottie_player_module": {
						return (
							<Suspense key={module._uid} fallback={null}>
								<LottiePlayerModule
									{...(module as Queries.LottiePlayerModuleFragment)}
								/>
							</Suspense>
						);
					}
					case "carry_positions": {
						return (
							<CarryPositionsModule
								key={module._uid}
								{...(module as Queries.CarryPositionsFragment)}
							/>
						);
					}
					default: {
						return null;
					}
				}
			})
			.filter(Boolean);
	}, [modules, siteConfig.store]);

	return moduleComponents;
};

export default ModuleRenderer;
